// ─────────────────────────────────────────────────────────────
// BehaviorAnalytics
// Renders the output of runAllDetectors() — measurable behavioral
// patterns with sample size, confidence, and estimated $ cost.
//
// Account selection is owned by the parent (Analytics.js), so this
// component just takes `trades` (already filtered to the selected
// account) and renders.
// ─────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from "react";
import { Card } from "../../components/ui/Card";
import { SectionTitle } from "../../components/ui/SectionTitle";
import { Pill } from "../../components/ui/Pill";
import { C } from "../../lib/colors";
import { fmt$ } from "../../lib/format";
import { useDetectors } from "../../hooks/useDetectors";
import { writeSnapshots, getTargetDescriptor, computeProgress } from "../../lib/detectorState";
import { useDetectorStates } from "../../hooks/useDetectorStates";
import { useDetectorSnapshotsMap, getSeries } from "../../hooks/useDetectorSnapshotsMap";
import { DetectorActionsMenu } from "./DetectorActionsMenu";
import { Sparkline } from "../../components/Sparkline";
import { useViewport } from "../../hooks/useViewport";
import { InsufficientStatePanel, EarlySignalBanner } from "../../components/DetectorProgress";

// Confidence label → color + tooltip
const CONFIDENCE_STYLE = {
  strong:       { color: C.green,  label: "Strong",      hint: "Large enough sample to act on." },
  moderate:     { color: C.blue,   label: "Moderate",    hint: "Sample is decent; pattern is suggestive." },
  thin:         { color: C.yellow, label: "Thin",        hint: "Small sample — treat as a hypothesis, not a conclusion." },
  insufficient: { color: C.muted,  label: "Insufficient", hint: "Not enough data yet to evaluate this pattern." },
};

function ConfidenceBadge({ confidence }) {
  const s = CONFIDENCE_STYLE[confidence] || CONFIDENCE_STYLE.insufficient;
  return (
    <span
      title={s.hint}
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 999,
        background: `${s.color}22`,
        color: s.color,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.3,
        textTransform: "uppercase",
        border: `1px solid ${s.color}55`,
      }}
    >
      {s.label}
    </span>
  );
}

function fmtPct(n) {
  if (n == null || isNaN(n)) return "—";
  return `${(n * 100).toFixed(0)}%`;
}

// ─── Detector card ─────────────────────────────────────────────────────
// One card per detector. Headline at top, summary line, then collapsible
// detail section with the data backing the claim.

function DetectorCard({ detector, state, accountId, onStatusChange, snapshotsMap, sinceDays, onSinceDaysChange }) {
  const { isMobile } = useViewport();
  const [open, setOpen] = useState(false);
  const hasCost = detector.estimated_cost > 0;
  const hasData = detector.confidence !== "insufficient";

  const status = state?.status || "active";
  const isAcknowledged = status === "acknowledged";
  const isWorking      = status === "working_on_it";
  // Dismissed cards are filtered out before reaching this component, but we
  // handle the prop just in case (e.g. rendered inside the Hidden section).
  const isDismissed    = status === "dismissed";

  // When the detector itself is INSUFFICIENT (low n, no real signal yet),
  // we don't double-treat the card with the acknowledged/dismissed dim:
  // the user can't act on insufficient data, so further visual de-emphasis
  // is noise. Status is still stored — it'll matter again when sample grows.
  // (Dismissed cards live in the Hidden section either way, so dim there is
  // expected; just suppress the acknowledged dim on insufficient.)
  const dim = (isAcknowledged && !isDismissed && detector.confidence !== "insufficient")
              || isDismissed;
  // Pills are also suppressed on insufficient cards for the same reason.
  const showStatusPill = detector.confidence !== "insufficient";

  // Working-on-it: pull data for the progress banner
  const descriptor = isWorking ? getTargetDescriptor(detector.id) : null;
  const currentMetric = descriptor ? descriptor.extract(detector) : null;
  const progress = descriptor && state?.target_value != null && state?.target_baseline != null
    ? computeProgress({
        direction: descriptor.direction,
        baseline:  Number(state.target_baseline),
        current:   currentMetric,
        target:    Number(state.target_value),
      })
    : null;

  return (
    <Card style={{ padding: 0, opacity: dim ? 0.55 : 1, transition: "opacity 0.2s ease" }}>
      {/* Working-on-it progress banner — renders ABOVE the header */}
      {isWorking && descriptor && (
        <div style={{
          padding: "10px 20px",
          background: `${C.green}10`,
          borderBottom: `1px solid ${C.green}33`,
          fontSize: 12,
          display: "flex",
          flexDirection: "column",
          gap: 6,
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", color: C.green, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, fontSize: 10 }}>
            <span>→ Working on this</span>
            {state.target_date && <span style={{ color: C.muted }}>by {state.target_date}</span>}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", color: C.text }}>
            <span>{descriptor.label}</span>
            <span style={{ fontFamily: "monospace" }}>
              <span style={{ color: C.muted }}>{descriptor.formatter(state.target_baseline)}</span>
              {" → "}
              <span style={{ color: C.text, fontWeight: 700 }}>{descriptor.formatter(currentMetric)}</span>
              {" → "}
              <span style={{ color: C.green }}>{descriptor.formatter(state.target_value)}</span>
            </span>
          </div>
          {progress != null && (
            <div style={{ height: 4, background: "#0b0d19", borderRadius: 2, overflow: "hidden" }}>
              <div style={{
                width: `${Math.max(0, Math.min(100, progress * 100))}%`,
                height: "100%",
                background: progress >= 1 ? C.green : progress >= 0.5 ? C.yellow : C.blue,
                transition: "width 0.3s ease",
              }} />
            </div>
          )}
        </div>
      )}

      {/* Header */}
      <div
        style={{
          padding: "16px 20px",
          borderBottom: open ? `1px solid ${C.border}` : "none",
          display: "flex",
          alignItems: isMobile ? "flex-start" : "flex-start",
          flexDirection: isMobile ? "column" : "row",
          gap: 16,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: 220 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>
              {detector.title}
            </h3>
            <ConfidenceBadge confidence={detector.confidence} />
            {showStatusPill && isAcknowledged && (
              <StatusPill color={C.blue} label="Acknowledged" stamp={state.acknowledged_at} />
            )}
            {showStatusPill && isDismissed && (
              <StatusPill color={C.muted} label="Dismissed" stamp={state.dismissed_at} />
            )}
          </div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
            {detector.subtitle}
          </div>
          {detector.confidence === "thin" && <EarlySignalBanner detector={detector} />}
          <div style={{ fontSize: 13, color: C.text, lineHeight: 1.5 }}>
            {detector.summary}
          </div>
        </div>

        {/* Cost + sample size + kebab */}
        <div style={{ minWidth: isMobile ? 0 : 140, textAlign: isMobile ? "left" : "right", display: "flex", flexDirection: "column", alignItems: isMobile ? "flex-start" : "flex-end" }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 4, marginBottom: 4 }}>
            {hasCost && !dim && (
              <div>
                <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Est. cost / month
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: C.red, fontFamily: "monospace", marginTop: 4 }}>
                  {fmt$(-detector.estimated_cost)}
                </div>
              </div>
            )}
            <DetectorActionsMenu
              detector={detector}
              state={state}
              accountId={accountId}
              onChange={onStatusChange}
            />
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: hasCost ? 8 : 0 }}>
            n = {detector.n}
            {detector.n_required > 0 && detector.confidence !== "strong" && (
              <> / {detector.n_required} for strong</>
            )}
          </div>
        </div>
      </div>

      {/* Insufficient-data state: progress bar + what's coming, instead of
          showing an empty/dead card body. Replaces the old "nothing here"
          impression for trial/new users still building a trade history. */}
      {!hasData && <InsufficientStatePanel detector={detector} />}

      {/* Expand toggle + detail body */}
      {hasData && (
        <>
          <button
            onClick={() => setOpen(o => !o)}
            style={{
              width: "100%",
              padding: "10px 20px",
              background: "transparent",
              border: "none",
              borderTop: open ? "none" : `1px solid ${C.border}`,
              color: C.muted,
              fontSize: 12,
              cursor: "pointer",
              textAlign: "left",
              fontWeight: 600,
            }}
          >
            {open ? "▼ Hide details" : "▶ Show details"}
          </button>
          {open && (
            <div style={{ padding: "0 20px 20px" }}>
              <SparklineSection
                detector={detector}
                state={state}
                snapshotsMap={snapshotsMap}
                sinceDays={sinceDays}
                onSinceDaysChange={onSinceDaysChange}
              />
              <DetectorDetails detector={detector} />
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ─── SparklineSection ──────────────────────────────────────────────────
// Renders a sparkline of this detector's primary metric over the chosen
// range, plus the 30/60/90 range selector. The range selector is hoisted
// to the parent so all cards stay in sync — switching to 60 days affects
// every sparkline at once.
//
// We derive metric + formatter from the TARGET_DEFAULTS registry by
// detector ID. If a detector isn't registered there, we render nothing.
function SparklineSection({ detector, state, snapshotsMap, sinceDays, onSinceDaysChange }) {
  const { isMobile } = useViewport();
  const descriptor = getTargetDescriptor(detector.id);
  if (!descriptor) return null;

  const series = getSeries(snapshotsMap, detector.id, descriptor.metric);
  const target = state?.status === "working_on_it" && state?.target_value != null
    ? Number(state.target_value)
    : null;

  return (
    <div style={{
      borderBottom: `1px solid ${C.border}`,
      paddingBottom: 14,
      marginBottom: 14,
    }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 8,
      }}>
        <div>
          <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>
            {descriptor.label}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
            Last {sinceDays} days · {series.length} snapshot{series.length === 1 ? "" : "s"}
          </div>
        </div>
        <RangeSelector value={sinceDays} onChange={onSinceDaysChange} />
      </div>

      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: isMobile ? "flex-start" : "flex-end",
        flexDirection: isMobile ? "column" : "row",
        gap: 16,
      }}>
        <Sparkline
          points={series}
          width={isMobile ? undefined : 220}
          height={56}
          direction={descriptor.direction}
          target={target}
          formatter={descriptor.formatter}
          ariaLabel={`${detector.title} ${descriptor.label} sparkline`}
          style={isMobile ? { width: "100%" } : undefined}
        />
        <div style={{ fontSize: 11, textAlign: "right", color: C.muted, lineHeight: 1.6 }}>
          {series.length >= 1 && (
            <div>
              <span>latest </span>
              <span style={{ color: C.text, fontFamily: "monospace", fontWeight: 700 }}>
                {descriptor.formatter(series[series.length - 1].value)}
              </span>
            </div>
          )}
          {target != null && (
            <div>
              <span>target </span>
              <span style={{ color: C.green, fontFamily: "monospace", fontWeight: 700 }}>
                {descriptor.formatter(target)}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── RangeSelector ──────────────────────────────────────────────────────
// 30 / 60 / 90 day pill toggle. Hoisted into the parent so all cards
// share one selection.
function RangeSelector({ value, onChange }) {
  return (
    <div style={{
      display: "inline-flex",
      gap: 0,
      border: `1px solid ${C.border}`,
      borderRadius: 6,
      overflow: "hidden",
    }}>
      {[30, 60, 90].map(d => {
        const active = value === d;
        return (
          <button
            key={d}
            onClick={() => onChange(d)}
            style={{
              padding: "4px 10px",
              fontSize: 11,
              fontWeight: 600,
              background: active ? C.blue : "transparent",
              color: active ? "#0b0d19" : C.muted,
              border: "none",
              cursor: "pointer",
            }}
          >
            {d}d
          </button>
        );
      })}
    </div>
  );
}

// ─── Detail bodies per detector ────────────────────────────────────────
// Each detector ships its own `details` shape, so we route by id.

function DetectorDetails({ detector }) {
  switch (detector.id) {
    case "revenge":             return <RevengeDetails d={detector.details} />;
    case "size_tilt":           return <SizeTiltDetails d={detector.details} />;
    case "win_streak_euphoria": return <WinStreakEuphoriaDetails d={detector.details} />;
    case "hour_cliff":          return <HourCliffDetails d={detector.details} />;
    case "day_of_week":         return <DayOfWeekDetails d={detector.details} />;
    case "time_in_trade":       return <TimeInTradeDetails d={detector.details} />;
    default:
      return <pre style={{ fontSize: 11, color: C.muted, overflow: "auto" }}>{JSON.stringify(detector.details, null, 2)}</pre>;
  }
}

function StatRow({ label, value, valueColor }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 12, color: C.muted }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: valueColor || C.text, fontFamily: "monospace" }}>{value}</span>
    </div>
  );
}

function RevengeDetails({ d }) {
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <StatRow label="Revenge trades win rate" value={fmtPct(d.revenge_winrate)} valueColor={C.red} />
        <StatRow label="Baseline win rate"       value={fmtPct(d.baseline_winrate)} valueColor={C.green} />
        <StatRow label="Avg net per revenge trade" value={fmt$(d.revenge_avg_pnl)} valueColor={d.revenge_avg_pnl >= 0 ? C.green : C.red} />
        <StatRow label="Avg net per baseline trade" value={fmt$(d.baseline_avg_pnl)} valueColor={d.baseline_avg_pnl >= 0 ? C.green : C.red} />
        <StatRow label="Total net from revenge trades" value={fmt$(d.revenge_total_pnl)} valueColor={d.revenge_total_pnl >= 0 ? C.green : C.red} />
      </div>
      {d.examples && d.examples.length > 0 && (
        <div>
          <SectionTitle>Recent examples</SectionTitle>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 8 }}>
            {d.examples.map((e, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "6px 8px", background: "#0b0d19", borderRadius: 6 }}>
                <span style={{ color: C.muted, fontFamily: "monospace" }}>{e.date} {e.time}</span>
                <span style={{ color: C.text }}>{e.symbol || "—"}</span>
                <Pill value={e.net} />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SizeTiltDetails({ d }) {
  const maxSize = Math.max(...d.buckets.map(b => b.avg_size || 0), 1);
  return (
    <div>
      <div style={{ marginBottom: 12, fontSize: 12, color: C.muted }}>
        Average position size after consecutive losses.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {d.buckets.map((b, i) => {
          const pct = (b.avg_size / maxSize) * 100;
          return (
            <div key={i}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                <span style={{ color: C.muted }}>{b.label} <span style={{ opacity: 0.6 }}>({b.n})</span></span>
                <span style={{ fontFamily: "monospace", color: C.text }}>{b.avg_size.toFixed(2)}</span>
              </div>
              <div style={{ height: 8, background: "#0b0d19", borderRadius: 4, overflow: "hidden" }}>
                <div style={{
                  width: `${pct}%`,
                  height: "100%",
                  background: i === 0 ? C.blue : i >= 2 ? C.red : C.yellow,
                  transition: "width 0.3s ease",
                }} />
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 16 }}>
        <StatRow label="Tilt ratio" value={d.tilt_ratio.toFixed(2) + "x"} valueColor={d.has_tilt ? C.red : C.green} />
        <StatRow label="Net P&L attributable to extra size" value={fmt$(d.extra_exposure_pnl)} valueColor={d.extra_exposure_pnl >= 0 ? C.green : C.red} />
      </div>
    </div>
  );
}

function HourCliffDetails({ d }) {
  if (!d.all_slots || d.all_slots.length === 0) {
    return <div style={{ fontSize: 12, color: C.muted }}>No slots with enough trades.</div>;
  }
  // Sort by total_net ascending so worst is first
  const ranked = [...d.all_slots].sort((a, b) => a.total_net - b.total_net);
  return (
    <div>
      <div style={{ marginBottom: 12, fontSize: 12, color: C.muted }}>
        30-minute slots, ranked by net $ damage (worst first). Overall win rate: <span style={{ color: C.text, fontFamily: "monospace" }}>{fmtPct(d.overall_winrate)}</span>
      </div>
      <div className="fos-scroll-x">
        <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 280 }}>
          {ranked.map((s, i) => (
            <div key={s.slot} style={{
              display: "grid",
              gridTemplateColumns: "70px 60px 1fr 90px",
              gap: 10,
              alignItems: "center",
              fontSize: 12,
              padding: "6px 8px",
              background: i === 0 && s.total_net < 0 ? "#2a0e16" : "#0b0d19",
              borderRadius: 6,
            }}>
              <span style={{ fontFamily: "monospace", color: C.text }}>{s.slot}</span>
              <span style={{ color: C.muted }}>n={s.n}</span>
              <span style={{ color: C.muted }}>WR {fmtPct(s.win_rate)}</span>
              <Pill value={s.total_net} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DayOfWeekDetails({ d }) {
  if (!d.rows || d.rows.length === 0) {
    return <div style={{ fontSize: 12, color: C.muted }}>No data.</div>;
  }
  // Order by day_index so display is Mon→Fri (skipping empty days)
  const ordered = [...d.rows].sort((a, b) => a.day_index - b.day_index);
  return (
    <div>
      <div className="fos-scroll-x">
        <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 280 }}>
          {ordered.map(r => (
            <div key={r.day} style={{
              display: "grid",
              gridTemplateColumns: "60px 60px 1fr 100px",
              gap: 10,
              alignItems: "center",
              fontSize: 12,
              padding: "6px 8px",
              background: "#0b0d19",
              borderRadius: 6,
            }}>
              <span style={{ color: C.text, fontWeight: 600 }}>{r.day}</span>
              <span style={{ color: C.muted }}>n={r.n}</span>
              <span style={{ color: C.muted }}>WR {fmtPct(r.win_rate)}</span>
              <Pill value={r.total_net} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function WinStreakEuphoriaDetails({ d }) {
  // Mirror of SizeTiltDetails. Same bucketed-bars layout, but inverted:
  // baseline = after 0 wins (just lost, or first trade), tallest = after 3+ wins.
  // Color coding flips too — large size after a *win* streak is yellow/green-ish
  // until it actually shows net-negative impact, in which case it goes red.
  const maxSize = Math.max(...d.buckets.map(b => b.avg_size || 0), 1);
  const exposureIsCostly = d.extra_exposure_pnl < 0;
  return (
    <div>
      <div style={{ marginBottom: 12, fontSize: 12, color: C.muted }}>
        Average position size after consecutive wins.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {d.buckets.map((b, i) => {
          const pct = (b.avg_size / maxSize) * 100;
          // i=0 is the baseline (post-loss). Tilt cells go yellow then escalate
          // to red ONLY if exposure has been costly; otherwise stay yellow/green.
          const barColor = i === 0
            ? C.blue
            : exposureIsCostly && i >= 2
              ? C.red
              : C.yellow;
          return (
            <div key={i}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
                <span style={{ color: C.muted }}>{b.label} <span style={{ opacity: 0.6 }}>({b.n})</span></span>
                <span style={{ fontFamily: "monospace", color: C.text }}>{(b.avg_size || 0).toFixed(2)}</span>
              </div>
              <div style={{ height: 8, background: "#0b0d19", borderRadius: 4, overflow: "hidden" }}>
                <div style={{
                  width: `${pct}%`,
                  height: "100%",
                  background: barColor,
                  transition: "width 0.3s ease",
                }} />
              </div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 16 }}>
        <StatRow label="Tilt ratio" value={d.tilt_ratio.toFixed(2) + "x"} valueColor={d.has_tilt ? C.yellow : C.green} />
        <StatRow
          label="Net P&L attributable to extra size"
          value={fmt$(d.extra_exposure_pnl)}
          valueColor={d.extra_exposure_pnl >= 0 ? C.green : C.red}
        />
      </div>
      {d.extra_exposure_pnl >= 0 && d.has_tilt && (
        <div style={{ marginTop: 12, fontSize: 11, color: C.muted, fontStyle: "italic" }}>
          Note: larger sizing after wins has been net-positive so far in your data.
          This is observational only — a streak of bad luck on a tilted trade can
          still hurt more than usual.
        </div>
      )}
    </div>
  );
}

function TimeInTradeDetails({ d }) {
  // Side-by-side average + median for wins vs losses. Render durations as
  // human-friendly strings rather than raw seconds.
  function fmtDuration(s) {
    if (s == null || s < 0) return "—";
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = Math.floor(s % 60);
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${sec}s`;
    return `${sec}s`;
  }
  return (
    <div>
      <div style={{ marginBottom: 12, fontSize: 12, color: C.muted }}>
        How long you hold trades, split by outcome.
      </div>
      <div className="fos-grid-2col" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
        <div style={{ padding: 12, background: "#0b0d19", borderRadius: 8, borderLeft: `3px solid ${C.green}` }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>WINNERS</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.green, fontFamily: "monospace" }}>
            {fmtDuration(d.avg_win_seconds)}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
            median {fmtDuration(d.median_win_seconds)} · n={d.win_count}
          </div>
        </div>
        <div style={{ padding: 12, background: "#0b0d19", borderRadius: 8, borderLeft: `3px solid ${C.red}` }}>
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>LOSERS</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: C.red, fontFamily: "monospace" }}>
            {fmtDuration(d.avg_loss_seconds)}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
            median {fmtDuration(d.median_loss_seconds)} · n={d.loss_count}
          </div>
        </div>
      </div>
      <StatRow
        label="Loss-to-win duration ratio"
        value={d.ratio.toFixed(2) + "x"}
        valueColor={d.has_tilt ? C.red : C.green}
      />
      {d.skipped_cross_midnight > 0 && (
        <div style={{ marginTop: 10, fontSize: 11, color: C.muted, fontStyle: "italic" }}>
          {d.skipped_cross_midnight} trade{d.skipped_cross_midnight === 1 ? "" : "s"} skipped (exit_time appears before entry_time — likely crossed midnight).
        </div>
      )}
    </div>
  );
}

// Small inline status pill used in the card header.
function StatusPill({ color, label, stamp }) {
  const stampStr = stamp ? new Date(stamp).toLocaleDateString() : null;
  return (
    <span
      title={stampStr ? `${label} ${stampStr}` : label}
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        background: `${color}22`,
        color,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        border: `1px solid ${color}55`,
      }}
    >
      {label}
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────

export function BehaviorAnalytics({ trades = [], userId, accountId }) {
  const { data: result, loading: detectorsLoading, error: detectorsError } = useDetectors({
    suite: "behavior",
    trades,
    userId,
  });
  // Wrapped in useMemo so the empty-array fallback has a stable identity
  // across renders — without it, the useEffect below that depends on
  // `detectors` would re-fire every render because [] !== [] by reference.
  const detectors = useMemo(() => result?.detectors ?? [], [result]);

  // All hooks must run unconditionally on every render. Keep them at the top
  // of the component, BEFORE any early returns (Rules of Hooks).
  const [showHidden, setShowHidden] = useState(false);
  // Sparkline range — applied uniformly across all detector cards
  const [sinceDays, setSinceDays] = useState(30);

  // Phase 6c — pull detector_state rows for this user. The hook keeps an
  // optimistic local copy that we mutate on click, then writes through to
  // Supabase. resolve() returns the effective row for a given (account, detector).
  const { rows: stateRows, resolve, setStatus } = useDetectorStates(userId);

  // Phase 6d — eager batched fetch of all detector_snapshots for this scope.
  // One query at tab mount means every card's sparkline is already populated
  // by the time the user clicks "Show details". Re-fetches when sinceDays
  // changes (30/60/90 selector).
  const { map: snapshotsMap } = useDetectorSnapshotsMap({
    userId,
    accountId: accountId ?? null,
    sinceDays,
  });

  // Phase 6 — silently write today's snapshots to detector_snapshots table.
  // Idempotent UPSERT; safe to run on every detector recompute.
  useEffect(() => {
    if (!userId) return;
    if (!detectors || detectors.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        await writeSnapshots({ userId, accountId: accountId ?? null, results: detectors });
        if (cancelled) return;
      } catch (err) {
        console.error("[BehaviorAnalytics] writeSnapshots failed:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [userId, accountId, detectors]);

  // Build view-model: pair each detector with its current state, partition
  // dismissed cards into a "hidden" section. Memo depends on `stateRows`
  // (the actual data) so optimistic state updates from the hook re-trigger
  // this partition. `resolve` has stable identity by design — depending on
  // it alone would skip re-renders.
  const { visible, dismissed } = useMemo(() => {
    const v = [], d = [];
    for (const det of (detectors || [])) {
      const st = resolve({ accountId: accountId ?? null, detectorId: det.id });
      if (st.status === "dismissed") d.push({ det, st });
      else v.push({ det, st });
    }
    return { visible: v, dismissed: d };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detectors, stateRows, accountId]);

  // Recompute the "actionable total" so cost projections from dismissed and
  // acknowledged detectors don't inflate the headline. We keep ALL
  // 'working_on_it' cards counted in the total — they're explicit goals.
  const headlineCost = useMemo(() => {
    return visible
      .filter(({ st }) => st.status !== "acknowledged")
      .reduce((s, { det }) => s + (det.estimated_cost || 0), 0);
  }, [visible]);

  if (!trades || trades.length === 0) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
          No trades for this account yet. Log a few and check back — patterns become measurable around 10–20 trades.
        </div>
      </Card>
    );
  }

  const anyActionable = headlineCost > 0;

  // Wrapped handler. The hook does optimistic updates; this just hands
  // it the click args, no rerender plumbing needed.
  const handleStatusChange = (args) => setStatus(args);

  if (detectorsLoading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 60, color: "#8892a4", fontSize: 14 }}>
        <span style={{ marginRight: 10 }}>⚙️</span> Analyzing behavioral patterns…
      </div>
    );
  }

  if (detectorsError && detectorsError !== "subscription_required") {
    return (
      <div style={{ padding: 32, textAlign: "center", color: "#ff6b6b", fontSize: 13 }}>
        Error loading detectors: {detectorsError}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Headline */}
      <Card>
        <SectionTitle>Behavioral Patterns</SectionTitle>
        <p style={{ color: C.muted, fontSize: 12, margin: "4px 0 12px" }}>
          Measurable patterns from your actual trade history. Each pattern shows
          its sample size, confidence level, and an estimated dollar cost if it
          continues unchanged. Acknowledged and dismissed patterns are excluded
          from the combined cost below.
        </p>
        {anyActionable ? (
          <div style={{
            background: "#2a0e16",
            border: `1px solid ${C.red}55`,
            borderRadius: 10,
            padding: "12px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 12,
          }}>
            <div>
              <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5 }}>
                Combined estimated cost
              </div>
              <div style={{ fontSize: 13, color: C.text, marginTop: 2 }}>
                Observed rate, extrapolated to a monthly figure
              </div>
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: C.red, fontFamily: "monospace" }}>
              {fmt$(-headlineCost)}<span style={{ fontSize: 12, color: C.muted, fontWeight: 400 }}> / month</span>
            </div>
          </div>
        ) : (
          <div style={{
            background: "#0d2a16",
            border: `1px solid ${C.green}55`,
            borderRadius: 10,
            padding: "12px 16px",
            color: C.text,
            fontSize: 13,
          }}>
            No costly patterns detected in your current sample. Keep at it.
          </div>
        )}
      </Card>

      {/* Visible detector cards (sorted by cost desc) */}
      {visible.map(({ det, st }) => (
        <DetectorCard
          key={det.id}
          detector={det}
          state={st}
          accountId={accountId ?? null}
          onStatusChange={handleStatusChange}
          snapshotsMap={snapshotsMap}
          sinceDays={sinceDays}
          onSinceDaysChange={setSinceDays}
        />
      ))}

      {/* Hidden / dismissed section */}
      {dismissed.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <button
            onClick={() => setShowHidden(s => !s)}
            style={{
              width: "100%",
              padding: "10px 12px",
              background: "transparent",
              border: `1px dashed ${C.border}`,
              borderRadius: 10,
              color: C.muted,
              fontSize: 12,
              cursor: "pointer",
              textAlign: "left",
              fontWeight: 600,
            }}
          >
            {showHidden ? "▼" : "▶"} Hidden patterns ({dismissed.length})
          </button>
          {showHidden && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 12 }}>
              {dismissed.map(({ det, st }) => (
                <DetectorCard
                  key={det.id}
                  detector={det}
                  state={st}
                  accountId={accountId ?? null}
                  onStatusChange={handleStatusChange}
                  snapshotsMap={snapshotsMap}
                  sinceDays={sinceDays}
                  onSinceDaysChange={setSinceDays}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
